<script lang="ts" setup>
import { Atropos } from 'atropos/vue'
import type { Repo } from '@prisma/client'
import { useLocaleThemePath } from '~/composables/locale'
import type { LordIcon } from '~/types/assets'
import { LordIconSource, LordIconTrigger, Iconify } from '~/types/assets'

const { repo } = defineProps<{
  repo: Repo
  lazy: boolean
}>()

const serverIcon: LordIcon = {
  src: LordIconSource.server,
  trigger: LordIconTrigger.loop,
  revert: true,
  delay: 500,
  size: 65,
  loading: 'lazy'
}

function getLanguageIcon(lang: string | null): string {
  const key: string | null = lang?.toLowerCase() || null
  return key && key in Iconify ? Iconify[key as keyof typeof Iconify] : Iconify.default
}
</script>

<template lang="pug">
Atropos(
  :rotate="true"
  :active-offset="40"
  :shadow-scale="1.05"
)
  .repo-card
    .repo-card__hero
      NuxtImg(
        v-if="repo?.asset?.nuxtPath || repo?.asset?.thumb"
        :src="repo?.asset?.nuxtPath || repo?.asset?.thumb"
        :provider="repo?.asset?.provider"
        :alt="$t('components.repo-card.image.alt', { repo: repo.name })"
        :title="$t('components.repo-card.image.title', { repo: repo.name })"
        :aria-label="$t('components.repo-card.image.aria-label', { repo: repo.name })"
        :longdesc="$t( \
          'components.repo-card.image.longdesc', \
          { \
            repo: repo.name, \
            description: repo?.description \
              ? $t('components.repo-card.image.longdesc-description', { description: repo.description }) \
              : '', \
          }, \
        )"
        width="400"
        height="300"
        :loading="lazy ? 'lazy' : 'eager'"
      )
      .repo-card__hero__404(v-else)
        .repo-card__hero__404__icon
          IconLord(v-bind="serverIcon")
      .language-badge
        Icon(:name="getLanguageIcon(repo?.language || null)")
      .github-badge
        Icon(:name="Iconify.github")
    .repo-card__body
      h6 {{ repo?.name }}
      .repo-card__body__meta(v-if="repo?.createdAt")
        span {{ $dayjs?.(repo?.createdAt)?.format('DD-MM-YYYY') }}
      p {{ repo?.description }}
    .repo-card__footer
      NuxtLink(
        :to="useLocaleThemePath(`/git/${repo?.name}`)",
        :title="$t('git.card.anchor.title', { name: repo?.slug})"
        :aria-label="`${repo?.slug} ${$t('git.card.anchor.aria-label')}`"
      ) {{ $t('components.repo-card.inspect') }}
</template>

<style lang="sass" scoped>
.repo-card
  @apply w-[325px] xl:w-[375px] h-full border border-dark-900 bg-[rgba(0,0,0,0.4)] overflow-hidden
  clip-path: polygon(10% 0, 90% 0, 100% 10%, 100% 100%, 80% 100%, 20% 100%, 0 100%, 0 10%)
  &:hover
    @apply scale-90
  .repo-card__hero
    @apply w-full flex items-center justify-center overflow-hidden relative
    img
      @apply w-full h-full max-h-[150px] object-cover
      pointer-events: none
    .repo-card__hero__404
      @apply w-full h-[150px] bg-hex bg-dark-800 flex-center
      .repo-card__hero__404__icon
        @apply w-fit
    .github-badge,
    .language-badge
      @apply absolute bottom-0 bg-primary text-dark-900 p-1 pb-0
      span.iconify
        @apply bg-dark-900 text-2xl
    .github-badge
      @apply right-0 rounded-tl-[50%] pl-2.5
    .language-badge
      @apply left-0 rounded-tr-[50%] pr-2.5
  .repo-card__body
    h6
      @apply w-full text-lg bg-dark-900 whitespace-normal text-dark-300 text-center font-semibold border-y border-primary
    p
      @apply w-full text-xs text-dark-200 text-center p-2
    .repo-card__body__meta
      @apply flex justify-end px-2 text-dark-400 text-sm
  .repo-card__footer
    @apply w-full p-1 bg-black text-primary border border-primary text-primary text-center font-bold cursor-pointer hover:bg-dark-900 hover:text-primary
    a
      @apply block w-full text-center text-sm
</style>
